import { comparePieceNumbers } from '@hector/piece-number-sort';
import type { ResolvedAgentUiAction } from '@hector/shared-types';
import type { SelectedDocument } from '@/features/bordereau/types/bordereau.types';

type BordereauAction = Extract<ResolvedAgentUiAction, { id: 'bordereau.open_selection_for_stamping' }>;
type WizardExitAction = Extract<
  ResolvedAgentUiAction,
  { id: 'bordereau.navigate_current_to_stamping' | 'bordereau.navigate_current_to_numbering_review' }
>;

export interface BordereauActionDependencies {
  readonly cabinetId: string | null;
  readonly fetchDocuments: (caseId: string) => Promise<SelectedDocument[]>;
  readonly openPanel: (documents: SelectedDocument[], caseId: string) => void;
}

function stampingIneligibility(row: SelectedDocument): string[] {
  const sourceDocumentType = row.sourceDocumentType?.trim();
  const pieceNumberReason = typeof row.sourcePieceNumber !== 'string'
    ? row.sourcePieceNumber === undefined
      ? 'piece_number=missing'
      : row.sourcePieceNumber === null
        ? 'piece_number=null'
        : `piece_number=invalid_type(${typeof row.sourcePieceNumber})`
    : row.sourcePieceNumber.trim().length === 0
      ? 'piece_number=empty'
      : null;
  return [
    row.documentCategory !== 'nos_pieces' ? `category=${row.documentCategory ?? 'missing'}` : null,
    row.sourceIsFolder !== false ? `is_folder=${row.sourceIsFolder ?? 'missing'}` : null,
    !sourceDocumentType || sourceDocumentType === 'synthesis'
      ? `document_type=${sourceDocumentType || 'missing'}`
      : null,
    row.originalDocumentId !== null ? `original_document_id=${row.originalDocumentId ?? 'missing'}` : null,
    !row.filePath?.trim() ? 'file_path=missing' : null,
    pieceNumberReason,
  ].filter((reason): reason is string => reason !== null);
}

/**
 * Les deux boutons de sortie du wizard restent dans le transcript local du fil : les
 * retrouver sur /assistant ou dans le dock, où aucun wizard n'est monté, les faisait
 * échouer au clic (signalement du 2026-10-29). Hors du wizard, ils ouvrent donc le
 * bordereau sur les pièces numérotées, lues au clic : l'étape Pièces montre la
 * numérotation, et le tamponnage reste derrière le bouton « Continuer ».
 */
export function isWizardExitAction(action: ResolvedAgentUiAction): action is WizardExitAction {
  return action.id === 'bordereau.navigate_current_to_stamping'
    || action.id === 'bordereau.navigate_current_to_numbering_review';
}

/** Même sélection que `all_numbered` côté backend, dans l'ordre naturel des numéros. */
export function selectNumberedPieces(action: WizardExitAction, rows: SelectedDocument[]): BordereauAction {
  const documentIds = rows
    .filter((row) => stampingIneligibility(row).length === 0)
    .sort((a, b) => comparePieceNumbers(a.piece_number ?? '', b.piece_number ?? ''))
    .map(({ id }) => id);
  if (documentIds.length === 0) {
    throw new Error(`Cannot open bordereau from ${action.id}: no numbered piece left in Nos pièces; caseId=${action.caseId}. Number the pieces, then ask the agent for a new button.`);
  }
  return {
    id: 'bordereau.open_selection_for_stamping',
    label: action.label,
    documentIds,
    caseId: action.caseId,
  };
}

export async function openBordereauSelectionForStamping(
  action: BordereauAction,
  dependencies: BordereauActionDependencies,
): Promise<void> {
  if (!dependencies.cabinetId) {
    throw new Error(`Cannot open bordereau selection: caseId=${action.caseId}; current cabinet is unavailable. Select a cabinet and retry.`);
  }

  const rows = await dependencies.fetchDocuments(action.caseId);
  const byId = new Map(rows.map((row) => [row.id, row]));
  const missingIds = action.documentIds.filter((id) => !byId.has(id));
  if (missingIds.length > 0) {
    throw new Error(`Cannot open bordereau selection: caseId=${action.caseId}; missing or ineligible documentIds=${missingIds.join(',')}. Refresh the conversation after numbering the pieces.`);
  }
  const freshSelection = action.documentIds.map((id) => byId.get(id)!);
  for (const row of freshSelection) {
    const invalidReasons = stampingIneligibility(row);
    if (invalidReasons.length > 0) {
      throw new Error(`Cannot open bordereau selection: caseId=${action.caseId}; documentId=${row.id}; stale eligibility failed (${invalidReasons.join(', ')}). Refresh after placing an eligible numbered file in Nos pièces.`);
    }
  }

  // This action is catalogued as non-mutating. The explicit Continue button owns
  // creation/persistence and the transition to stamping.
  dependencies.openPanel(freshSelection, action.caseId);
}
